import React from 'react';
import { TemplateType } from '@server/trading-templates/schemas/input.schema';
import type { TradingTemplate } from '@server/trading-templates/schemas/output.schema';
import { useWidget } from '@client/widgets/widget.hook';
import { useTemplateManagement } from '@client/features/trading-templates/use-template-management.hook';
import { useSharedTemplateActions } from '@client/features/trading-templates/shared-template-actions.hook';
import { TemplateBuilderPanel } from '@client/features/trading-templates/template-builder-panel';

const signalBlockTemplateType: TemplateType = 'signalBlock';

export const SignalBlocksBuilder: React.FC = () => {
  const { openWidget } = useWidget();
  const {
    isLoading,
    handleDeleteTemplates,
    handleToggleDefault,
    handleDuplicateTemplate,
    getTemplateById
  } = useTemplateManagement(signalBlockTemplateType);

  const handleOpenEditorForTemplate = (
    templateId: string | undefined,
    templateName: string,
    initialConfig?: TradingTemplate['config'],
    onSaved?: (savedTemplate: {
      templateId: string;
      name: string;
      config: TradingTemplate['config'];
    }) => void
  ) => {
    const template =
      templateId && !initialConfig ? getTemplateById(templateId) : undefined;
    const config = initialConfig ?? template?.config;
    if (!config || config.type !== 'signalBlock') return;

    openWidget({
      type: 'SignalBlockTemplateEditor',
      templateId,
      initialName: templateName,
      initialConditions: config.value.conditions,
      onSaved
    });
  };

  const openCreateEditor = () =>
    openWidget({
      type: 'SignalBlockTemplateEditor'
    });

  const {
    handleShare,
    handleImportShared,
    handleModifyShared,
    handleRevokeShared
  } = useSharedTemplateActions({
    templateType: signalBlockTemplateType,
    openTemplateEditor: ({
      templateId,
      templateName,
      initialConfig,
      onSaved
    }) =>
      handleOpenEditorForTemplate(
        templateId,
        templateName,
        initialConfig,
        onSaved
      )
  });

  return (
    <TemplateBuilderPanel
      templateType={signalBlockTemplateType}
      isLoading={isLoading}
      onNew={openCreateEditor}
      onDelete={handleDeleteTemplates}
      onSetAsDefault={handleToggleDefault}
      onModify={handleOpenEditorForTemplate}
      onDuplicate={handleDuplicateTemplate}
      onShare={(templateId) => handleShare(templateId)}
      onImportShared={handleImportShared}
      onModifyShared={(sharedResourceId) => handleModifyShared(sharedResourceId)}
      onRevokeShared={handleRevokeShared}
    />
  );
};
